import React from 'react';
import { Box } from '@mui/material';
import theme from 'theme/theme';
import { Model } from '../../hooks/useModelState';
import { PCNode } from '../../MineModel/Nodes';
import PCSpool from '../../MineModel/Spools/PCSpool';
import { IntersectionData } from '../../MineModel/intersectionTypes';
import { SceneProps } from '../scene-utils';
import { Scene } from '../types';
import { createSingleClickGate } from '../singleClickGate';
import sideDrawerContentFunction from './BulkEditSceneSideDrawer';
import ModelDrawerTrigger from '../../ModelDrawerTrigger';
import { getBulkEditSceneState, subscribeToBulkEditSceneState, updateBulkEditSceneState } from './bulkEditSceneState';
import { clearIsolate } from './bulkEditIsolate';
import { removeIsolateFilters, resetIsolateStage } from '../StopeSelectedScene/StopeSelectedSceneActions';

const TITLE = 'BULK EDIT';

// A double-click on a pipe focuses the camera, so the spool dialog waits until the click is known to be single.
const spoolEditGate = createSingleClickGate();

const BulkEditDisplay = ({ props }: { props: SceneProps }) => {
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);

  React.useEffect(() => {
    const unsubscribe = subscribeToBulkEditSceneState(() => forceUpdate());
    return unsubscribe;
  }, []);

  return (
    <>
      {!props.context.state.drawerOpen && (
        <Box sx={{ position: 'absolute', right: theme.spacing(2), zIndex: 20 }}>
          <ModelDrawerTrigger handleClick={() => props.context.actions.setDrawerOpen(true)} />
        </Box>
      )}
    </>
  );
};

const BulkEditScene = (props: SceneProps): Scene => {
  const display = () => <BulkEditDisplay props={props} />;

  const getCanvasHandlers = (model: Model) => {
    const openNodeForm = (node: PCNode) => {
      spoolEditGate.cancel();
      model.setSelectedNode(node);
      props.context.actions.editNodes(true);
    };
    // Only the flow-loop marker reaches this: MergedLineMesh routes a click on the pipe itself through
    // onIntersections when that handler is present, and returns before calling onSelect. Selecting the
    // marker's spool is the one way to edit a loop whose spool cannot be picked - two nodes at the same
    // coordinates, or a spool lying under its own neighbours.
    const selectSpool = (spool: PCSpool) => {
      const { entityType } = getBulkEditSceneState();
      updateBulkEditSceneState({
        selectedIds: [spool.spoolId],
        entityType: entityType === 'Node' || entityType === 'Stope' ? 'Spool' : entityType,
      });
    };
    return {
      onNodeHover: () => { },
      onNodeSelect: openNodeForm,
      onSpoolSelect: selectSpool,
      // A click near a node lands on the node; only a single click on bare pipe opens the spool.
      onIntersections: (intersections: IntersectionData[]) => {
        const nodeHit = intersections.find((i) => i.type === 'node' && i.node);
        if (nodeHit?.node) {
          openNodeForm(nodeHit.node);
          return;
        }
        const spool = intersections.find((i) => i.type === 'spool' && i.spool)?.spool;
        if (spool) spoolEditGate.click(() => props.context.actions.editSpool(spool));
      },
    };
  };

  const sideDrawerContent = (model: Model) => sideDrawerContentFunction(props, model);

  const onExit = (model: Model) => {
    spoolEditGate.cancel();
    clearIsolate(model);
    removeIsolateFilters(model);
    resetIsolateStage();
  };

  return {
    key: props.key,
    display,
    getCanvasHandlers,
    sideDrawerContent,
    onExit,
    title: TITLE,
    actionButtons: [],
  };
};

export default BulkEditScene;
